import React from 'react';

import kebabCase from 'lodash/kebabCase';
import qs from 'query-string';
import isString from 'lodash/isString';
import { domToReact, attributesToProps, type DOMNode, Element } from 'html-react-parser';
import { withImage } from '@lucid/hooks';
import { isTag, isText, type Node, type ChildNode, hasChildren } from 'domhandler';
import { ElementType } from 'domelementtype';
import { isWistiaEmbed } from '@lucid/utils';

import Image from '../components/image/image';
import Link, { type LinkProps } from '../components/link/link';
import WistiaEmbed from '../components/wistia/wistia-embed';
import VideoPlayer from '../components/video/video-player/video-player';
import ImageWithPlaceholder from '../components/image/image-with-placeholder';
import type { Lucid } from '../types/types';
import type { ReactHTMLReplaceOptions } from './reactHTMLParser';

type LinkData = {
  data?: LinkProps;
};

type LucidDOMNode = DOMNode & LinkData;

// finds the text in any children under an anchor tag
// removes all children tags as well as we don't want extra tags within link
export const findAnchorText = (domNode: Node[]): string =>
  domNode.reduce((previous, child) => {
    if (child instanceof Element && hasChildren(child)) return `${previous}${findAnchorText(child.children)}`;
    if (isText(child)) return `${previous}${child.data}`;

    return previous;
  }, '');

const hasMatchingChildren = (type: string, children: ChildNode[]) =>
  children.some(child => child instanceof Element && child.type === ElementType.Tag && child.name === type);

// Checks recursively if the children and the children's children have a matching tag
const hasNestedMatchingChildren = (type: string, children: ChildNode[]): boolean =>
  children.some(child => {
    if (child instanceof Element && child.children?.length) return hasNestedMatchingChildren(type, child.children);

    return child.type === ElementType.Tag && child.name === type;
  });

const replaceParser = (domNode: LucidDOMNode, index?: number, options?: ReactHTMLReplaceOptions) => {
  const headingTagRegex = /(h[23])/gim;

  if (domNode instanceof Element && domNode.attribs) {
    const { headingItemId, isDiscovery, replaceTag, onLoad } = options || {};
    const { attribs, children, name, parent } = domNode;

    // Add ID to headings for table of contents in HTML blog posts
    if (headingItemId && headingTagRegex.test(name) && !attribs.id)
      return domToReact([
        {
          ...domNode,
          attribs: { ...attribs, id: `heading_${headingItemId}_${(index || 0) + 1}` },
        },
      ] as unknown as DOMNode[]);

    // Used to correctly display Wistia videos
    if (name === 'script') {
      const { src } = attribs;

      // We want to strip out the Wistia script tag as we inject it globally
      if (src === 'https://fast.wistia.com/assets/external/E-v1.js') return <></>;

      if (isWistiaEmbed(src)) {
        const wistiaID = /[^/]*$/.exec(src)![0].replace('.jsonp', '');

        return <WistiaEmbed key={wistiaID} hashedId={wistiaID} />;
      }

      return domNode;
    }

    // Used to fix height on Wistia videos still using iframe
    if (name === 'iframe') {
      const { src, ...rest } = attribs;
      const props = attributesToProps(rest);

      // Handle Youtube embeds for Discovery
      if (isDiscovery && src?.split('/').includes('www.youtube.com')) {
        const splitUrl = src?.split('/');
        const idValue = splitUrl.findIndex((str: string) => str === 'embed') as number;
        const idIndex = idValue + 1;
        const videoId = splitUrl[idIndex]?.split('?')[0];

        return (
          <VideoPlayer
            controls
            placeholder={() => <ImageWithPlaceholder alt="" src={`https://i3.ytimg.com/vi/${videoId}/hqdefault.jpg`} />}
            url={src}
          />
        );
      }

      // Check for Wistia iframe embeds
      if (isWistiaEmbed(src, 'iframe')) {
        const wistiaID = /[^/]*$/.exec(src)![0].replace('.jsonp', '');

        return <WistiaEmbed {...props} key={`iframe-${wistiaID}`} isIFrame hashedId={wistiaID} />;
      }
    }

    if (isTag(domNode)) {
      const isImage = name === 'img';

      // Wistia Image Parser, we don't want to display the image
      if (isImage && isWistiaEmbed(attribs.src)) return <></>;

      // Image Parser
      if (isImage) {
        const { src, alt, width } = attribs;

        const customCss = {} as { width?: string };

        if (width) {
          customCss.width = `${width}px`;
        }

        if (isString(src)) {
          const currentImage = qs.parseUrl(src);
          customCss.width = currentImage.query?.width ? `${currentImage.query?.width}px` : undefined;
        }

        const hasCustomStyles = width || customCss.width;

        return (
          <Image
            key={kebabCase(src)}
            className={hasCustomStyles ? 'overwrite-css' : ''}
            css={customCss}
            imageAlt={alt || ''}
            imageSrc={withImage({
              filename_disk: src,
            } as Lucid.Directus.Generated.Directus_Files)}
            loading="lazy"
            onLoad={onLoad}
          />
        );
      }

      // Link Parser
      if (name === 'a' && parent) {
        const props = attributesToProps(attribs);

        // Set class for image links on Discovery pages
        if (hasMatchingChildren('img', children) && isDiscovery) {
          domNode.attribs.class = `${domNode.attribs.class || ''} img-link`;
        }

        if (props?.href && children?.length) {
          const anchorText = findAnchorText(children);
          const hasImgChild = hasMatchingChildren('img', children);

          if (!(anchorText || hasImgChild)) return <></>;

          const { href } = attribs;

          return (
            <Link
              {...props}
              key={kebabCase(anchorText)}
              excludeFromGatsby={domNode.data?.excludeFromGatsby}
              to={href}
              underline="hover"
              variant="primary"
            >
              {hasImgChild ? domToReact(children as DOMNode[], { ...options, replace: replaceParser }) : anchorText}
            </Link>
          );
        }
      }

      // Paragraph Parser
      if (name === 'p') {
        // If there are no children in the p tag, return empty fragment
        if (!children?.length) return <></>;

        const props = attributesToProps(attribs);

        // Paragraph Parser for nested links
        if (hasMatchingChildren('a', children)) {
          children.forEach(child => {
            if (isTag(child) && child.name === 'a') {
              if (
                child.attribs?.href?.includes('/lucidchart/editNewOrRegister/') ||
                child.attribs?.href?.includes('/lucidscale/editNewOrRegister/') ||
                child.attribs?.href?.includes('/lucidspark/editNewOrRegister/') ||
                child.attribs?.href?.includes('/documents/editNewOrRegister/')
              ) {
                child.attribs.rel = 'nofollow';
              }

              return domToReact(children as DOMNode[], { ...options, replace: replaceParser });
            }
          });
        }

        // If children have an image tag and is a discovery page, add table-div class
        if (isDiscovery && hasMatchingChildren('img', children))
          return (
            <div {...props} className={`${domNode.attribs.class || ''} table-div`}>
              {domToReact(children as DOMNode[], { ...options, replace: replaceParser })}
            </div>
          );

        if (replaceTag === 'span')
          return <span {...props}>{domToReact(children as DOMNode[], { ...options, replace: replaceParser })}</span>;

        return <p {...props}>{domToReact(children as DOMNode[], { ...options, replace: replaceParser })}</p>;
      }

      // Div Parser
      if (name === 'div') {
        // If there are no children in the div tag, return empty fragment
        if (!children?.length) return <></>;

        // Remove nested Wistia divs, but not nested iframes
        if (!hasNestedMatchingChildren('iframe', children) && attribs?.class?.startsWith('wistia_')) return <></>;

        // Wistia Parser find nested iframe
        return domToReact(children as DOMNode[], { ...options, replace: replaceParser });
      }

      // If <strong> tag is found and is a discovery page, change it to <h4>
      if (name === 'strong' && isDiscovery)
        return (
          <h4 style={{ fontWeight: 500, display: 'inline', fontSize: '20px' }}>
            {domToReact(children as DOMNode[], { ...options, replace: replaceParser })}
          </h4>
        );
    }
  }

  return domNode;
};

export default replaceParser;
